/**
 * @file Reads the user documents the Overview names: each person's display
 * name and current teams, including the caller's own teams for the team-level
 * goal. Consumers: the Overview panel and its tables, the page's panel view,
 * the level titles and Home. A name is the user's `name`, else `email`, else
 * the uid, and a null uid is "Unassigned". A person's teams are decided only
 * by `teamsOfUser`, from their `businessGroups` and
 * `parameters/analytics.teamBusinessGroups`, so an unmarked group is no team.
 * `resolved` is true only once every listed document and the parameters have
 * been read, so an empty team list is final only then.
 */
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { teamsOfUser, type User } from '#types';

import { analyticsParametersStore } from './analyticsParametersStore';
import type { AnalyticsUsers } from './index.types';
import { useAnalyticsDocsByPaths } from './useAnalyticsDocsByPaths';

const pathOf = (uid: string): string => `users/${uid}`;

export const useAnalyticsUsers = (uids: readonly string[]): AnalyticsUsers => {
  useEffect(() => {
    void analyticsParametersStore.connect();
    return () => analyticsParametersStore.disconnect();
  }, []);
  const parameters = useSnapshot(analyticsParametersStore.state);
  const { data, loading } = useAnalyticsDocsByPaths<User>(
    uids.filter((uid) => uid !== '').map(pathOf),
  );
  const teamBusinessGroups = parameters.data?.teamBusinessGroups;

  return {
    nameOf: (uid) => {
      if (uid === null) return 'Unassigned';
      const user = data.get(pathOf(uid));
      return user?.name || user?.email || uid;
    },
    teamsOf: (uid) =>
      teamsOfUser(data.get(pathOf(uid))?.businessGroups, teamBusinessGroups),
    resolved: !loading && !parameters.loading,
  };
};
